/**
 * The Sales page's "Text" box (RingCentral) — beside Email, in the feed's
 * composer. Sends from the main number as ext 101 (POST /api/leads/:id/texts);
 * the text then appears in the feed as one entry, like an email.
 *
 * One press, one text (idempotency key, useAction). RingCentral counts 1000
 * UTF-16 units — an emoji is two — which is what String.length counts too.
 * Only a Canadian (+1) number can be texted; the server says the same.
 *
 * TEMPLATES (070): the picker fills a text template for this lead with the
 * email templates' merge engine (GET /api/leads/:id/merge-fields, filled by
 * lib/mergeFields fillTextTemplate) and puts it in the box to edit. A value the
 * lead does not have becomes "[no move date]" in the words, and Send stays off
 * until every such gap is filled in or removed (the server refuses one too).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
import { useAction } from '../hooks/useAction';
import { formatPhone } from '../lib/format';
import { fillTextTemplate, textHasGaps } from '../lib/mergeFields';

const GAP_RE = /\[no [^\]]+\]|\{\{\s*[a-z_]+\s*\}\}/gi;

export function TextCompose({ leadId, phone, canText, optedOut, configured, loading, maxLength, onClose, onSent }: {
  leadId: string;
  phone: string | null | undefined;
  canText: boolean;
  /** The number opted out of texts (071, mirrored from RingCentral): no sending. */
  optedOut?: boolean;
  configured: boolean;
  /** The lead's texting details are still loading: no verdict yet. */
  loading?: boolean;
  maxLength: number;
  onClose: () => void;
  onSent: () => void;
}) {
  const api = useApiClient();
  const send = useAction();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<{ id: string; name: string; body: string }[]>([]);
  const [filling, setFilling] = useState(false);
  const merge = useRef<Record<string, string | null> | null>(null);
  const box = useRef<HTMLTextAreaElement | null>(null);

  // The company's text templates. None (or migration 070 not run): no picker.
  useEffect(() => {
    let alive = true;
    api.getTextTemplates().then((r) => { if (alive) setTemplates(r.data?.templates ?? []); }).catch(() => { /* no picker */ });
    return () => { alive = false; };
  }, [api]);
  useEffect(() => { merge.current = null; }, [leadId]);

  const applyTemplate = async (id: string) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    if (draft.trim() && !window.confirm(`Replace what you have written with "${t.name}"?`)) return;
    setError(null);
    setFilling(true);
    try {
      if (!merge.current) merge.current = (await api.getMergeFields(leadId)).data ?? {};
      setDraft(fillTextTemplate(t.body, merge.current).text);
      requestAnimationFrame(() => box.current?.focus());
    } catch (e: any) {
      setError(e?.message || 'Could not fill that template for this lead.');
    } finally {
      setFilling(false);
    }
  };

  const length = draft.trim().length;
  const tooLong = length > maxLength;
  const gaps = textHasGaps(draft) ? [...new Set(draft.match(GAP_RE) ?? [])] : [];
  const ready = configured && canText && !optedOut && length > 0 && !tooLong && gaps.length === 0;

  const onSend = () => send.run(async (key) => {
    if (!ready) return;
    setError(null);
    try {
      await api.withIdempotencyKey(key).sendLeadText(leadId, draft.trim());
      setDraft('');
      onSent();
    } catch (e: any) {
      setError(e?.message || 'The text was not sent.');
    }
  });

  const blocked = loading
    ? 'Loading…'
    : !configured
    ? 'Texting is not set up on this server yet.'
    : !canText
      ? 'This lead has no Canadian phone number to text. Add one in the header.'
      : optedOut
        ? 'Opted out of texts: this client replied STOP, so RingCentral will not deliver texts to this number until they reply START. Call or email them instead.'
        : null;

  return (
    <div style={{ background: 'var(--crm-comm-text-tint)', border: '1px solid var(--crm-comm-text-line)', borderLeft: '3px solid var(--crm-comm-text)', borderRadius: '10px', padding: '12px', marginBottom: '12px', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-comm-text)' }}>
          Text {phone ? formatPhone(phone) : ''} <span style={{ fontWeight: 400, color: 'var(--crm-muted)' }}>· from the main number</span>
        </span>
        <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', fontSize: '12px', padding: 0 }}>Close</button>
      </div>
      {blocked && (
        <div style={{ fontSize: '12px', color: optedOut ? 'var(--crm-red)' : 'var(--crm-amber)', marginBottom: '6px' }}>
          {optedOut && !loading && configured && canText && <OptedOutBadge />} {blocked}
        </div>
      )}
      {!blocked && templates.length > 0 && (
        <select value="" onChange={(e) => { const id = e.target.value; e.target.value = ''; if (id) void applyTemplate(id); }}
                disabled={filling || send.pending} aria-label="Templates"
                style={{ marginBottom: '6px', maxWidth: '100%', padding: '5px 8px', fontSize: '12px', borderRadius: '6px', background: 'var(--crm-field)', color: 'var(--crm-text)', border: '1px solid var(--crm-field-line)' }}>
          <option value="">{filling ? 'Filling in…' : 'Templates…'}</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}
      <textarea
        ref={box}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSend(); } }}
        rows={3}
        autoFocus
        disabled={!!blocked || send.pending}
        placeholder={blocked ? '' : 'Your text… (Ctrl/⌘ + Enter to send)'}
        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '13px', borderRadius: '7px', resize: 'vertical', background: 'var(--crm-field)', color: 'var(--crm-text)', border: `1px solid ${tooLong ? 'var(--crm-red-line)' : gaps.length ? 'var(--crm-amber-line)' : 'var(--crm-field-line)'}` }}
      />
      {gaps.length > 0 && (
        <div style={{ fontSize: '12px', color: 'var(--crm-amber)', marginTop: '4px' }}>
          Fill in or remove before sending: {gaps.join(', ')}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '6px' }}>
        <span style={{ fontSize: '11px', color: tooLong ? 'var(--crm-red)' : 'var(--crm-dim)' }}>{length} / {maxLength}</span>
        <button type="button" onClick={onSend} disabled={!ready || send.pending}
                style={{ padding: '7px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, border: 'none', background: 'var(--crm-comm-text)', color: 'var(--crm-on-fill)', cursor: ready && !send.pending ? 'pointer' : 'not-allowed', opacity: ready && !send.pending ? 1 : 0.5 }}>
          {send.pending ? 'Sending…' : 'Send text'}
        </button>
      </div>
      {error && <div role="alert" style={{ fontSize: '12px', color: 'var(--crm-red)', marginTop: '6px' }}>{error}</div>}
    </div>
  );
}

/** "Opted out of texts" — the lead header and the Text box (071). */
export function OptedOutBadge() {
  return (
    <span title="The client replied STOP. RingCentral will not deliver texts to this number until they reply START."
          style={{ display: 'inline-block', fontSize: '10px', fontWeight: 700, color: 'var(--crm-red)', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', padding: '1px 7px', borderRadius: '4px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
      Opted out of texts
    </span>
  );
}
